/**
 * Cartão do guia passo a passo (protótipo do Claude Design, 28/10/2026): o marco ("2 de 4 · Monte
 * a busca", que abre a lista dos marcos), os quatro trechos, "⋯" (Preciso de ajuda, Começar de
 * novo, Sair do guia) e recolher. Recolhido ou quieto, vira a pílula "Guia · 2 de 4".
 *
 * Fica no canto de baixo à direita, se arrasta pela faixa de cima (ou pelo "Mover", com as
 * setas), desvia do que não pode cobrir e fica transparente quando cobre algo (pedido do
 * Marcelo, 28/10/2026; regras em lugar.ts e useLugarDoCartao.ts).
 *
 * A troca de passo é anunciada (`aria-live="polite"`) sem tirar o foco de onde a pessoa está. O
 * cartão não prende o foco. Movimento: entra com .ds-entra; o texto do passo troca no lugar com
 * .ds-aparece (com "reduzir movimento", só fade).
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useGuiaText, type GuiaKey } from './guiaText';
import { useLugarDoCartao } from './useLugarDoCartao';

export interface AcaoDoCartao {
  rotulo: string;
  onClick: () => void;
}

export interface ConteudoCartao {
  frase: string;
  texto?: string;
  /** Contatos da busca em andamento. */
  conta?: number;
  aviso?: { titulo: string; texto: string };
  dica?: { texto: string; acao?: AcaoDoCartao };
  botao?: AcaoDoCartao;
  botao2?: AcaoDoCartao;
  link?: AcaoDoCartao;
  espera?: string;
  ok?: boolean;
}

const MARCOS: GuiaKey[] = ['m1', 'm2', 'm3', 'm4'];

interface GuiaCartaoProps {
  /** 0 a 3; 4 é concluído. */
  marco: number;
  /** Muda a cada passo: o texto troca no lugar. */
  chave: string;
  conteudo: ConteudoCartao | null;
  pilula: boolean;
  /** Seletores que o cartão não cobre nem no lugar escolhido pela pessoa. */
  evitar: string[];
  onAbrir: () => void;
  onRecolher: () => void;
  onAjuda: () => void;
  onRecomecar: () => void;
  onSair: () => void;
}

export function GuiaCartao({
  marco,
  chave,
  conteudo,
  pilula,
  evitar,
  onAbrir,
  onRecolher,
  onAjuda,
  onRecomecar,
  onSair,
}: GuiaCartaoProps): JSX.Element {
  const { tx } = useGuiaText();
  const [marcosAbertos, setMarcosAbertos] = useState(false);
  const [menu, setMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const lugar = useLugarDoCartao(evitar, chave, menu || marcosAbertos);
  const feito = marco >= 4;
  const titulo = feito ? tx('done') : tx('step_of', { n: marco + 1, name: tx(MARCOS[marco]!) });

  useEffect(() => {
    if (!menu) return undefined;
    const fora = (e: MouseEvent): void => {
      if (!menuRef.current?.contains(e.target as Node)) setMenu(false);
    };
    const tecla = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setMenu(false);
    };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', tecla);
    };
  }, [menu]);

  if (pilula || !conteudo) {
    const texto = feito ? `${tx('card_label')} · ${tx('done')}` : tx('pill', { n: marco + 1 });
    return createPortal(
      <div className="gp-camada">
        <button
          ref={lugar.ref}
          type="button"
          className={`gp-pilula ds-entra${lugar.classes}`}
          style={lugar.estilo}
          aria-label={tx('pill_label', { text: texto })}
          onClick={() => {
            if (!lugar.foiArraste()) onAbrir();
          }}
          {...lugar.arrastar}
          {...lugar.opaco}
        >
          <span className="gp-marca" aria-hidden="true">
            <span className="material-symbols-rounded">explore</span>
          </span>
          {texto}
          <span className="material-symbols-rounded" aria-hidden="true">
            expand_less
          </span>
        </button>
      </div>,
      document.body,
    );
  }

  const trecho = (i: number): string =>
    feito || i < marco ? 'is-feito' : i === marco ? 'is-agora' : '';

  return createPortal(
    <div className="gp-camada">
      <section
        ref={lugar.ref}
        className={`gp-cartao ds-entra${lugar.classes}`}
        style={lugar.estilo}
        aria-label={tx('card_label')}
        {...lugar.opaco}
      >
        <div className="gp-cartao__topo" ref={menuRef} {...lugar.arrastar}>
          <button
            type="button"
            className="gp-marca gp-mover"
            data-mover=""
            aria-label={tx('move')}
            title={tx('move')}
            onKeyDown={lugar.onTeclaMover}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              explore
            </span>
          </button>
          <button
            type="button"
            className="gp-cartao__marco"
            aria-expanded={marcosAbertos}
            onClick={() => {
              setMarcosAbertos((v) => !v);
              setMenu(false);
            }}
          >
            <span>{titulo}</span>
            <span className="material-symbols-rounded" aria-hidden="true">
              {marcosAbertos ? 'expand_less' : 'expand_more'}
            </span>
          </button>
          <button
            type="button"
            className="gp-icone"
            aria-label={tx('card_more')}
            aria-haspopup="menu"
            aria-expanded={menu}
            onClick={() => {
              setMenu((v) => !v);
              setMarcosAbertos(false);
            }}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              more_horiz
            </span>
          </button>
          <button
            type="button"
            className="gp-icone"
            aria-label={tx('card_collapse')}
            onClick={onRecolher}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              expand_more
            </span>
          </button>
          {menu ? (
            <div className="gp-menu ds-entra" role="menu" aria-label={tx('card_more')}>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenu(false);
                  onAjuda();
                }}
              >
                <span className="material-symbols-rounded" aria-hidden="true">
                  help
                </span>
                {tx('menu_help')}
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenu(false);
                  onRecomecar();
                }}
              >
                <span className="material-symbols-rounded" aria-hidden="true">
                  restart_alt
                </span>
                {tx('menu_restart')}
              </button>
              {lugar.escolhido ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenu(false);
                    lugar.voltarAoCanto();
                  }}
                >
                  <span className="material-symbols-rounded" aria-hidden="true">
                    picture_in_picture_alt
                  </span>
                  {tx('menu_corner')}
                </button>
              ) : null}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenu(false);
                  onSair();
                }}
              >
                <span className="material-symbols-rounded" aria-hidden="true">
                  logout
                </span>
                {tx('menu_exit')}
              </button>
            </div>
          ) : null}
        </div>

        <div className="gp-trechos" aria-hidden="true">
          {MARCOS.map((m, i) => (
            <span key={m} className={trecho(i)} />
          ))}
        </div>

        {marcosAbertos ? (
          <ol className="gp-marcos" aria-label={tx('card_milestones')}>
            {MARCOS.map((m, i) => {
              const estado =
                feito || i < marco ? 'is-feito' : i === marco ? 'is-agora' : 'is-depois';
              return (
                <li key={m} className={estado}>
                  <span className="gp-marcos__n" aria-hidden="true">
                    {estado === 'is-feito' ? (
                      <span className="material-symbols-rounded">check</span>
                    ) : (
                      i + 1
                    )}
                  </span>
                  <span>{tx(m)}</span>
                  <span className="gp-sr">
                    {estado === 'is-feito'
                      ? ` ${tx('done_sr')}`
                      : estado === 'is-agora'
                        ? ` ${tx('now_sr')}`
                        : ''}
                  </span>
                </li>
              );
            })}
          </ol>
        ) : null}

        <div className="gp-cartao__corpo ds-aparece" key={chave} aria-live="polite">
          {conteudo.ok ? (
            <span className="gp-ok" aria-hidden="true">
              <span className="material-symbols-rounded">check</span>
            </span>
          ) : null}
          <p className="gp-frase">{conteudo.frase}</p>
          {conteudo.texto ? <p className="gp-texto">{conteudo.texto}</p> : null}
          {conteudo.conta !== undefined ? (
            <div className="gp-conta">
              <span className="gp-conta__giro ds-giro" aria-hidden="true" />
              <span>
                <b>{conteudo.conta.toLocaleString()}</b>{' '}
                <span>{tx(conteudo.conta === 1 ? 'count_one' : 'count_other')}</span>
              </span>
            </div>
          ) : null}
          {conteudo.aviso ? (
            <div className="gp-aviso" role="status">
              <span className="material-symbols-rounded" aria-hidden="true">
                warning
              </span>
              <span>
                <b>{conteudo.aviso.titulo}</b>
                <span>{conteudo.aviso.texto}</span>
              </span>
            </div>
          ) : null}
          {conteudo.dica ? (
            <p className="gp-dica">
              {conteudo.dica.texto}
              {conteudo.dica.acao ? (
                <>
                  {' '}
                  <button type="button" className="gp-link" onClick={conteudo.dica.acao.onClick}>
                    {conteudo.dica.acao.rotulo}
                  </button>
                </>
              ) : null}
            </p>
          ) : null}
          {conteudo.botao || conteudo.botao2 || conteudo.link ? (
            <div className="gp-acoes">
              {conteudo.botao ? (
                <button type="button" className="gp-btn" onClick={conteudo.botao.onClick}>
                  {conteudo.botao.rotulo}
                </button>
              ) : null}
              {conteudo.botao2 ? (
                <button
                  type="button"
                  className="gp-btn gp-btn--sec"
                  onClick={conteudo.botao2.onClick}
                >
                  {conteudo.botao2.rotulo}
                </button>
              ) : null}
              {conteudo.link ? (
                <button type="button" className="gp-link" onClick={conteudo.link.onClick}>
                  {conteudo.link.rotulo}
                </button>
              ) : null}
            </div>
          ) : null}
          {conteudo.espera ? (
            <p className="gp-espera">
              <span className="material-symbols-rounded" aria-hidden="true">
                touch_app
              </span>
              {conteudo.espera}
            </p>
          ) : null}
        </div>
      </section>
    </div>,
    document.body,
  );
}
